import {
  Header,
  HeaderTitle,
  mdBumps,
  pageEntranceSpringValues,
  PrimaryButton,
  xlBumps,
} from '@wrisk/ui-components'
import React, { FunctionComponent, useCallback } from 'react'
import { generatePath, useNavigate } from 'react-router'
import { animated, useSpring } from 'react-spring'

import {
  getAdditionalProfiles,
  getCurrentQuote,
  Profile,
  Proposal,
} from '../../../../../../domain'
import { useRemoveAdditionalProfileProposalAdjustment } from '../../../../../../hooks/adjustments/proposal'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../../infrastructure/internationalisation'
import { AdditionalProfileDisclosureConfig } from '../../../../../../state/configuration'
import { AdditionalProfileView } from '../../../../../organisms/quote'
import { useProposal } from '../../../ProposalProvider'
import { PageNotification } from '../../PageNotification'
import { AdditionalProfilePath } from '../path'

export interface AdditionalProfileSummaryProps {
  section: AdditionalProfileDisclosureConfig
  isLastSection: boolean
  onComplete: (proposal: Proposal) => void
}

const tKey = TKey('pages.additional-profile-summary')

export const AdditionalProfileSummary: FunctionComponent<
  AdditionalProfileSummaryProps
> = ({ section, isLastSection, onComplete }) => {
  const { t } = useWriskTranslation()

  const navigate = useNavigate()

  const spring = useSpring(pageEntranceSpringValues)
  const { proposal } = useProposal()

  const quote = getCurrentQuote(proposal)
  const additionalProfiles = getAdditionalProfiles(quote)

  const onUpdateCallback = useCallback(
    (profile: Profile) => {
      navigate(generatePath(AdditionalProfilePath.Profile, profile))
    },
    [navigate],
  )
  const onCompleteCallback = useCallback(
    () => onComplete(proposal),
    [onComplete, proposal],
  )
  const onRemoveCallback = useRemoveAdditionalProfileProposalAdjustment(isLastSection)
  const onAddCallback = useCallback(() => {
    navigate(generatePath(AdditionalProfilePath.NewProfile))
  }, [navigate])

  return (
    <animated.div id='additional-summary' style={spring}>
      <Header>
        <HeaderTitle>{t(tKey('header'))}</HeaderTitle>
      </Header>

      <PageNotification tKey={tKey} mb={mdBumps} />

      <AdditionalProfileView
        quote={quote}
        onUpdate={onUpdateCallback}
        onAdd={onAddCallback}
        onRemove={onRemoveCallback.execute}
        loading={onRemoveCallback.loading}
        maxAdditionalProfiles={section.maxAdditionalProfiles}
        mb={xlBumps}
      />

      <PrimaryButton
        type='submit'
        layout='fixed'
        mb={xlBumps}
        onClick={onCompleteCallback}
        data-testid='button-confirm'
      >
        {additionalProfiles.length
          ? t(tKey('actions.continue'))
          : t(tKey('actions.without'))}
      </PrimaryButton>
    </animated.div>
  )
}
